/* eslint-disable no-param-reassign */

import { camelizeKeys, keysToSnakeCase } from "lib/helpers";
import { actions } from "rdx";
import { api } from "rdx/services/api";
import SchemaValidator from "util/schema-helpers/SchemaValidator";
import { pickBy, identity } from "lodash";
import messages from "./messages";
import {
  GetOrgPreSalesResponseT,
  GetMoreOrgPreSalesParamsT,
  GetMoreOrgPostSalesParamsT,
  GetMoreOrgCompletedParamsT,
  GetOrgPostSalesResponseT,
  GetOrgCompletedResponseT,
  GetOrgPreSalesParamsT,
  GetOrgPostSalesParamsT,
  GetOrgCompletedParamsT,
} from "./orgProjectsTypes";
import { GetOrgPreSalesResponseSchema, GetOrgPostSalesResponseSchema, GetOrgCompletedResponseSchema } from "./schemas";
import { ORG_PROJECTS_TAGS } from "./tags";

const defaultOrgProjectsQueryParams = {
  sort: "activity_asc",
  status: "active",
  limit: 20,
};

export const orgProjectsApi = api
  .enhanceEndpoints({
    addTagTypes: [...Object.values(ORG_PROJECTS_TAGS)],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getOrgPreSalesProjects: builder.query<GetOrgPreSalesResponseT["body"], GetOrgPreSalesParamsT>({
        query: ({ filterParams }) => ({
          url: "projects_pre_sales/org",
          params: keysToSnakeCase({
            ...defaultOrgProjectsQueryParams,
            ...pickBy(filterParams, identity),
          }),
        }),
        transformResponse: (res: GetOrgPreSalesResponseT) => {
          SchemaValidator.validate<typeof GetOrgPreSalesResponseSchema>({
            schema: GetOrgPreSalesResponseSchema,
            schemaName: "GetOrgPreSalesResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(() => {
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ERROR_GETTING_ORG_PROJECTS,
                severity: "error",
              }),
            );
          });
        },
        providesTags: [ORG_PROJECTS_TAGS.ORG_PRE_SALES_PROJECTS],
      }),
      getMoreOrgPreSalesProjects: builder.query<GetOrgPreSalesResponseT["body"], GetMoreOrgPreSalesParamsT>({
        query: ({ p, cursor, filterParams }) => ({
          url: "projects_pre_sales/org",
          params: keysToSnakeCase({
            ...defaultOrgProjectsQueryParams,
            ...pickBy(filterParams, identity),
            p,
            ...(cursor && { cursor }),
          }),
        }),
        transformResponse: (res: GetOrgPreSalesResponseT) => {
          SchemaValidator.validate<typeof GetOrgPreSalesResponseSchema>({
            schema: GetOrgPreSalesResponseSchema,
            schemaName: "GetOrgPreSalesResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              dispatch(
                orgProjectsApi.util.updateQueryData(
                  "getOrgPreSalesProjects",
                  { filterParams: queryParams.filterParams },
                  (cachedData) => {
                    cachedData.projects.push(...data.projects);
                    cachedData.paging = data.paging;
                  },
                ),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_ORG_PROJECTS,
                  severity: "error",
                }),
              );
            });
        },
      }),
      getOrgPostSalesProjects: builder.query<GetOrgPostSalesResponseT["body"], GetOrgPostSalesParamsT>({
        query: ({ filterParams }) => ({
          url: "enterprise/projects",
          params: keysToSnakeCase({
            tab: "post_sales",
            ...defaultOrgProjectsQueryParams,
            ...pickBy(filterParams, identity),
          }),
        }),
        transformResponse: (res: GetOrgPostSalesResponseT) => {
          SchemaValidator.validate<typeof GetOrgPostSalesResponseSchema>({
            schema: GetOrgPostSalesResponseSchema,
            schemaName: "GetOrgPostSalesResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(() => {
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ERROR_GETTING_ORG_PROJECTS,
                severity: "error",
              }),
            );
          });
        },
        providesTags: [ORG_PROJECTS_TAGS.ORG_POST_SALES_PROJECTS],
      }),
      getMoreOrgPostSalesProjects: builder.query<GetOrgPostSalesResponseT["body"], GetMoreOrgPostSalesParamsT>({
        query: ({ p, filterParams }) => ({
          url: "enterprise/projects",
          params: keysToSnakeCase({
            tab: "post_sales",
            ...defaultOrgProjectsQueryParams,
            ...pickBy(filterParams, identity),
            p,
          }),
        }),
        transformResponse: (res: GetOrgPostSalesResponseT) => {
          SchemaValidator.validate<typeof GetOrgPostSalesResponseSchema>({
            schema: GetOrgPostSalesResponseSchema,
            schemaName: "GetOrgPostSalesResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              dispatch(
                orgProjectsApi.util.updateQueryData(
                  "getOrgPostSalesProjects",
                  { filterParams: queryParams.filterParams },
                  (cachedData) => {
                    cachedData.providerLeads.push(...data.providerLeads);
                    cachedData.paging = { ...data.paging, totalCount: cachedData.paging.totalCount };
                  },
                ),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_ORG_PROJECTS,
                  severity: "error",
                }),
              );
            });
        },
      }),
      getOrgCompletedProjects: builder.query<GetOrgCompletedResponseT["body"], GetOrgCompletedParamsT>({
        query: ({ filterParams }) => ({
          url: "enterprise/projects",
          params: keysToSnakeCase({
            tab: "completed",
            ...defaultOrgProjectsQueryParams,
            ...pickBy(filterParams, identity),
          }),
        }),
        transformResponse: (res: GetOrgCompletedResponseT) => {
          SchemaValidator.validate<typeof GetOrgCompletedResponseSchema>({
            schema: GetOrgCompletedResponseSchema,
            schemaName: "GetOrgCompletedResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(() => {
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ERROR_GETTING_ORG_PROJECTS,
                severity: "error",
              }),
            );
          });
        },
        providesTags: [ORG_PROJECTS_TAGS.ORG_COMPLETED_PROJECTS],
      }),
      getMoreOrgCompletedProjects: builder.query<GetOrgCompletedResponseT["body"], GetMoreOrgCompletedParamsT>({
        query: ({ p, filterParams }) => ({
          url: "enterprise/projects",
          params: keysToSnakeCase({
            tab: "completed",
            ...defaultOrgProjectsQueryParams,
            ...pickBy(filterParams, identity),
            p,
          }),
        }),
        transformResponse: (res: GetOrgCompletedResponseT) => {
          SchemaValidator.validate<typeof GetOrgCompletedResponseSchema>({
            schema: GetOrgCompletedResponseSchema,
            schemaName: "GetOrgCompletedResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              dispatch(
                orgProjectsApi.util.updateQueryData(
                  "getOrgCompletedProjects",
                  { filterParams: queryParams.filterParams },
                  (cachedData) => {
                    cachedData.providerLeads.push(...data.providerLeads);
                    cachedData.paging = { ...data.paging, totalCount: cachedData.paging.totalCount };
                  },
                ),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_ORG_PROJECTS,
                  severity: "error",
                }),
              );
            });
        },
      }),
    }),
  });

export const { useGetOrgPreSalesProjectsQuery, useGetOrgPostSalesProjectsQuery, useGetOrgCompletedProjectsQuery } =
  orgProjectsApi;
